import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchLearningModules,
    insertLearningModule,
    updateLearningModule,
    deleteLearningModule,
    updateLearningModuleStatus,
    updateLearningModuleICTId,
} from '../../../store/learningModulesSlice';
import { fetchICTData } from '../../../store/uiSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../Styles/Common.css';
const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role || null;
const isReadOnly = userRole === "sales";

const LearningModules = () => {
    const dispatch = useDispatch();
    const { modules } = useSelector((state) => state.learningModules);
    const { ictData } = useSelector((state) => state.ui);

    const [searchTerm, setSearchTerm] = useState('');
    const [filteredModules, setFilteredModules] = useState([]);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [isIctSearchPopupOpen, setIctSearchPopupOpen] = useState(false); // ICT search popup
    const [isEdit, setIsEdit] = useState(false);
    const [moduleForm, setModuleForm] = useState({
        ictId_id: '',
        moduleTitle: '',
        moduleDesc: '',
        moduleLab: '',
        moduleMT: '',
        status: 'A',
    });

    // ICT search states
    const [ictSearchTerm, setIctSearchTerm] = useState(''); // For ICT popup
    const [filteredICTs, setFilteredICTs] = useState([]);
    const [selectedIctId, setSelectedIctId] = useState(null); // Selected ICT ID for filtering modules

    // Row-level ICT search
    const [rowIctSearch, setRowIctSearch] = useState({});

    // Pagination and sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [sortBy, setSortBy] = useState('id'); // Default sort by ID

    useEffect(() => {
        dispatch(fetchLearningModules());
        dispatch(fetchICTData());
    }, [dispatch]);

    // Sorting and filtering logic
    useEffect(() => {
        let sortedModules = [...modules];

        if (sortBy === 'id') {
            sortedModules.sort((a, b) => a.id - b.id);
        } else if (sortBy === 'ictId_id') {
            sortedModules.sort((a, b) => a.ictId_id - b.ictId_id);
        } else if (sortBy === 'moduleTitle') {
            sortedModules.sort((a, b) =>
                a.moduleTitle.toLowerCase().localeCompare(b.moduleTitle.toLowerCase())
            );
        }

        const filtered = sortedModules.filter((module) => {
            const matchesSearch = module.moduleTitle
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
            const matchesIct = selectedIctId ? module.ictId_id === selectedIctId : true;
            return matchesSearch && matchesIct;
        });

        setFilteredModules(filtered);
        setCurrentPage(1); // Reset to first page on search or sort change
    }, [modules, searchTerm, sortBy, selectedIctId]);

    // ICT search logic for popup
    useEffect(() => {
        if (ictSearchTerm.length >= 3) {
            const filtered = ictData.filter((ict) =>
                ict.ictName.toLowerCase().includes(ictSearchTerm.toLowerCase())
            );
            setFilteredICTs(filtered);
        } else {
            setFilteredICTs([]);
        }
    }, [ictSearchTerm, ictData]);

    const handleOpenAddPopup = () => {
        setIsEdit(false); // Ensure the form is in "Add" mode
        setPopupOpen(true); // Open the popup
        setModuleForm({
            ictId_id: '',
            moduleTitle: '',
            moduleDesc: '',
            moduleLab: '',
            moduleMT: '',
            status: 'A',
        }); // Reset the form to default values
    };

    const handleEdit = (module) => {
        setIsEdit(true);
        setPopupOpen(true);
        setModuleForm(module); // Populate the form with the module's data
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEdit) {
                await dispatch(updateLearningModule(moduleForm)).unwrap();
                toast.success('Learning module updated successfully!');
            } else {
                await dispatch(insertLearningModule(moduleForm)).unwrap();
                toast.success('Learning module added successfully!');
            }
            setPopupOpen(false);
            setModuleForm({
                ictId_id: '',
                moduleTitle: '',
                moduleDesc: '',
                moduleLab: '',
                moduleMT: '',
                status: 'A',
            });
        } catch (error) {
            toast.error('Failed to save learning module.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this module?')) {
            try {
                await dispatch(deleteLearningModule(id)).unwrap();
                toast.success('Learning module deleted successfully!');
            } catch {
                toast.error('Failed to delete learning module.');
            }
        }
    };

    const handleStatusToggle = async (id, status) => {
        try {
            await dispatch(updateLearningModuleStatus({ id, status })).unwrap();
            toast.success(`Status updated to ${status === 'A' ? 'Active' : 'Deactivated'}!`);
        } catch (error) {
            toast.error('Failed to update status.');
        }
    };

    const handleSearch = (e) => setSearchTerm(e.target.value);

    const handleSortChange = (e) => setSortBy(e.target.value);

    const handleRowIctSearch = (id, searchValue) => {
        setRowIctSearch((prev) => ({
            ...prev,
            [id]: searchValue,
        }));

        if (searchValue.length >= 3) {
            const filtered = ictData.filter((ict) =>
                ict.ictName.toLowerCase().includes(searchValue.toLowerCase())
            );
            setFilteredICTs(filtered);
        } else {
            setFilteredICTs([]);
        }
    };

    const handleICTIdUpdate = async (id, ictId_id) => {
        try {
            await dispatch(updateLearningModuleICTId({ id, ictId_id })).unwrap();
            toast.success('ICT updated successfully!');
        } catch (error) {
            toast.error('Failed to update ICT.');
        }
    };

    const handleIctSearchSubmit = (ictId) => {
        setSelectedIctId(ictId);
        setIctSearchPopupOpen(false); // Close the ICT search popup
    };

    const handleClearFilter = () => {
        setSelectedIctId(null); // Reset the ICT filter
        setSearchTerm(''); // Clear the search term if needed
        setFilteredModules(modules); // Display all modules
    };
    

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredModules.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredModules.length / itemsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div className="container">
            <div className="header-container">
                <h3>Learning Modules Management</h3>
                {!isReadOnly && (
                <button className="add-lead-btn" onClick={handleOpenAddPopup}>
                    <i className="fa fa-plus"></i>
                </button>
                )}
            </div>
            <div className="feedback-filters">
                <input
                    type="text"
                    placeholder="Search by Title"
                    value={searchTerm}
                    onChange={handleSearch}
                    className="filter-input"
                />
                <select value={sortBy} onChange={handleSortChange} className="filter-input">
                    <option value="id">Sort by ID</option>
                    <option value="ictId_id">Sort by ICT ID</option>
                    <option value="moduleTitle">Sort by Title</option>
                </select>
                <button
                    className="add-btn fa fa-search"
                    onClick={() => setIctSearchPopupOpen(true)}
                ></button>
                <button 
                    className="add-btn fa fa-times" 
                    onClick={handleClearFilter}
                ></button>
            </div>
            <table className="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Lab</th>
                        <th>MT</th>
                        <th>ICT</th>
                        <th>Status</th>
                        {!isReadOnly && (
    <                       th>Actions</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {currentItems.map((module, index) => (
                        <tr key={module.id}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td>{module.moduleTitle}</td>
                            <td>{module.moduleDesc}</td>
                            <td>{module.moduleLab}</td>
                            <td>{module.moduleMT}</td>
                            <td>
                                {isReadOnly ? (
                                <span>
                                {
                                    ictData.find((ict) => ict.id === module.ictId_id)?.ictName ?? ''
                                }
                                </span>
                                    ) : (
                                <>
                                <input
                                    type="text"
                                    value={
                                        rowIctSearch[module.id] ??
                                        ictData.find((ict) => ict.id === module.ictId_id)?.ictName ?? ''
                                    }
                                    placeholder="Search ICT"
                                    onFocus={() => {
                                        setRowIctSearch((prev) => ({
                                            ...prev,
                                            [module.id]: ictData.find((ict) => ict.id === module.ictId_id)?.ictName ?? '',
                                        }));
                                    }}
                                    onChange={(e) => handleRowIctSearch(module.id, e.target.value)}
                                    className="ict-search-box"
                                />
                                {rowIctSearch[module.id]?.length >= 3 && (
                                    <div className="ict-search-results">
                                        {filteredICTs.map((ict) => (
                                            <div
                                                key={ict.id}
                                                onClick={() => {
                                                    handleICTIdUpdate(module.id, ict.id);
                                                    setRowIctSearch((prev) => ({
                                                        ...prev,
                                                        [module.id]: ict.ictName,
                                                    }));
                                                    setFilteredICTs([]);
                                                }}
                                                className="ict-search-result"
                                            >
                                                {ict.ictName}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                </>
                                )}
                            </td>
                            <td>
                                {isReadOnly ? (
                                <span>{module.status === "A" ? "Active" : "Inactive"}</span>
                                ) : (
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={module.status === 'A'}
                                        onChange={() =>
                                            handleStatusToggle(
                                                module.id,
                                                module.status === 'A' ? 'D' : 'A'
                                            )
                                        }
                                    />
                                    <span className="slider round"></span>
                                </label>
                                )}
                            </td>
                            {!isReadOnly && (
                            <td>
                                <button
                                    className="icon-btn update-btn fa fa-edit"
                                    onClick={() => handleEdit(module)}
                                ></button>
                                <button
                                    className="icon-btn delete-btn fa fa-trash"
                                    onClick={() => handleDelete(module.id)}
                                ></button>
                            </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="pagination">
                {[...Array(totalPages).keys()].map((page) => (
                    <button
                        key={page + 1}
                        className={`pagination-btn ${
                            currentPage === page + 1 ? 'active' : ''
                        }`}
                        onClick={() => handlePageChange(page + 1)}
                    >
                        {page + 1}
                    </button>
                ))}
            </div>
            {isPopupOpen && !isReadOnly &&(
                <div className="popup-overlay">
                    <div className="popup-content">
                        <h3>{isEdit ? 'Edit Module' : 'Add Module'}</h3>
                        <form onSubmit={handleSubmit} className="form-grid">
                            <div className="form-group">
                                <label>ICT</label>
                                <select
                                    name="ictId_id"
                                    value={moduleForm.ictId_id}
                                    onChange={(e) =>
                                        setModuleForm({ ...moduleForm, ictId_id: e.target.value })
                                    }
                                    required
                                >
                                    <option value="">Select ICT</option>
                                    {ictData.map((ict) => (
                                        <option key={ict.id} value={ict.id}>
                                            {ict.ictName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Title</label>
                                <input
                                    type="text"
                                    name="moduleTitle"
                                    value={moduleForm.moduleTitle}
                                    onChange={(e) =>
                                        setModuleForm({ ...moduleForm, moduleTitle: e.target.value })
                                    }
                                    required
                                />
                            </div>
                            <div className="form-group-lg">
                                <label>Description</label>
                                <textarea
                                    name="moduleDesc"
                                    value={moduleForm.moduleDesc}
                                    onChange={(e) =>
                                        setModuleForm({ ...moduleForm, moduleDesc: e.target.value })
                                    }
                                    required
                                ></textarea>
                            </div>
                            <div className="form-group">
                                <label>Lab</label>
                                <input
                                    type="text"
                                    name="moduleLab"
                                    value={moduleForm.moduleLab}
                                    onChange={(e) =>
                                        setModuleForm({ ...moduleForm, moduleLab: e.target.value })
                                    }
                                />
                            </div>
                            <div className="form-group">
                                <label>MT</label>
                                <input
                                    type="text"
                                    name="moduleMT"
                                    value={moduleForm.moduleMT}
                                    onChange={(e) =>
                                        setModuleForm({ ...moduleForm, moduleMT: e.target.value })
                                    }
                                />
                            </div>
                            <div className="form-group">
                                <label>Status</label>
                                <select
                                    name="status"
                                    value={moduleForm.status}
                                    onChange={(e) =>
                                        setModuleForm({ ...moduleForm, status: e.target.value })
                                    }
                                >
                                    <option value="A">Active</option>
                                    <option value="D">Deactivated</option>
                                </select>
                            </div>
                            <div className="form-actions">
                                <button type="submit" className="submit-btn">
                                    {isEdit ? <i className='fa fa-edit'></i> : <i className='fa fa-check-square'></i>}
                                </button>
                                <button
                                    type="button"
                                    className="cancel-btn fa fa-close"
                                    onClick={() => setPopupOpen(false)}
                                >
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isIctSearchPopupOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <h3>Filter by ICT Name</h3>
                        <input
                            type="text"
                            placeholder="Search ICT Name"
                            value={ictSearchTerm}
                            onChange={(e) => setIctSearchTerm(e.target.value)}
                            className="filter-input"
                        />
                        {ictSearchTerm.length >= 3 && (
                            <div className="ict-search-results">
                                {filteredICTs.map((ict) => (
                                    <div
                                        key={ict.id}
                                        onClick={() => handleIctSearchSubmit(ict.id)}
                                        className="ict-search-result"
                                    >
                                        {ict.ictName}
                                    </div>
                                ))}
                            </div>
                        )}
                        <button
                            className="cancel-btn fa fa-close"
                            onClick={() => setIctSearchPopupOpen(false)}
                        >
                        </button>
                    </div>
                </div>
            )}
            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
};

export default LearningModules;
